import { defineStore } from 'pinia';
import axios from 'axios';
// Import useRuntimeConfig from #imports for test compatibility
import useAuthStore from '~/stores/auth';
import useLmsStore from '~/lms';
import { handleApiRequest, defaultResponseStatus } from '~/functions/handleApiRequest';
import type { ResponseStatus } from '~/lms';

interface InteractiveContentStoreState {
   lmsApiBaseurl: string;
   /**
    * Whether we are currently sending completion data to the backend.
    * @deprecated Use submittingActivities instead for activity-specific state
    */
   isSendingCompletionData: boolean;
   /**
    * Track which activities are currently submitting completion data
    * Key: activity filename, Value: boolean indicating if submitting
    */
   submittingActivities: Record<string, boolean>;
   /**
    * Stores any error message encountered during the API call.
    */
   sendCompletionError: string | null;
   /**
    * Set by the init-store-config.ts Nuxt plugin
    */
   domain: string;
   /**
    * Response status for completion data submission
    */
   completionResponseStatus: ResponseStatus;
   /**
    * Track processed completion events to prevent duplicates
    */
   processedCompletionHashes: Set<string>;
}

interface InteractiveContentStoreActions {
   /**
    * Sends the interactive content completion event data to the backend API.
    * @param eventData - The data object received from the interactive content (e.g., xAPI statement).
    * @param courseSlug - The slug of the course (for progress updates).
    * @param lessonSlug - The slug of the lesson (for progress updates).
    * @param activityFilename - The filename of the activity for activity-specific loading state.
    * @param updateLessonCache - Optional callback to update TanStack Query lesson cache with activity status.
    */
   sendCompletionData: (eventData: unknown, courseSlug?: string, lessonSlug?: string, activityFilename?: string, updateLessonCache?: (updates: any) => void) => Promise<void>;
   /**
    * Checks if a specific activity is currently submitting completion data
    * @param activityFilename - The filename of the activity
    */
   isActivitySubmitting: (activityFilename: string) => boolean;
   /**
    * Generates a hash for completion event data to detect duplicates
    * @param eventData - The completion event data
    */
   generateCompletionHash: (eventData: unknown) => string;
   /**
    * Sends debug information to the backend API.
    * @param debugData - The debug data object containing H5P info.
    */
   sendDebugData: (debugData: unknown) => Promise<void>;
   /**
    * Fetches the H5P content HTML from the backend API.
    * @param encodedUrlPathFilename - The URL-encoded path to the H5P content file.
    * @param siteDomain - The domain requesting the content, used for the X-LMS-Requester header.
    * @returns The HTML content as a string.
    */
   fetchH5pContent: (encodedUrlPathFilename: string) => Promise<{
      htmlContent: string | null;
      statusCode: number | null;
      statusMessage: string | null;
   }>;
   /**
    * Updates progress after successful completion (non-blocking).
    * @param courseSlug - The slug of the course.
    * @param lessonSlug - The slug of the lesson.
    */
   updateProgressAfterCompletion: (courseSlug: string, lessonSlug: string) => void;
}

// No getters defined for now, but structure is ready if needed later.
interface InteractiveContentStoreGetters {}

const useInteractiveContentStore = defineStore<
   string,
   InteractiveContentStoreState,
   {},
   InteractiveContentStoreActions
>('interactiveContent', {
   state: (): InteractiveContentStoreState => {
      return {
         lmsApiBaseurl: '',
         isSendingCompletionData: false,
         submittingActivities: {},
         sendCompletionError: null,
         domain: '',
         completionResponseStatus: defaultResponseStatus,
         processedCompletionHashes: new Set<string>(),
      };
   },
   actions: {
      async fetchH5pContent(encodedUrlPathFilename) {
         const authStore = useAuthStore();
         if (!authStore.accessToken) {
            console.error(
               'Authentication token is missing for fetchH5pContent.',
            );
            throw new Error('Authentication token is missing.');
         }

         const apiUrl = `${this.lmsApiBaseurl}interactive-content/${encodedUrlPathFilename}`;
         try {
            const response = await axios.get<string>(apiUrl, {
               headers: {
                  // Use authStore getter for headers, ensuring Authorization is included
                  ...authStore.authHeaders,
                  // 'X-LMS-Requester': useAuthStore().domain,
               },
            });
            // Return success details
            return {
               htmlContent: response.data,
               statusCode: response.status,
               statusMessage: response.statusText,
            };
         } catch (error) {
            const decodedFilename = decodeURIComponent(encodedUrlPathFilename);
            console.error(
               `Error fetching H5P content "${decodedFilename}":`,
               error,
            );

            let statusCode: number | null = null;
            let statusMessage: string | null =
               'Failed to fetch interactive content.';

            if (axios.isAxiosError(error)) {
               statusCode = error.response?.status ?? null;
               // Attempt to get the message from the response data first
               const responseDataMessage = error.response?.data?.message;
               if (
                  typeof responseDataMessage === 'string' &&
                  responseDataMessage.trim() !== ''
               ) {
                  statusMessage = responseDataMessage;
               } else {
                  // Fallback to statusText or the general error message
                  statusMessage = error.response?.statusText ?? error.message;
               }
               console.error(
                  `Axios error details: Status ${statusCode}, Message: ${statusMessage}`,
               );
            } else if (error instanceof Error) {
               statusMessage = error.message; // Keep fallback for non-Axios errors
            }

            // Return error details
            return {
               htmlContent: null,
               statusCode: statusCode,
               statusMessage: statusMessage,
            };
         }
      },

      generateCompletionHash(eventData: unknown): string {
         // Create a hash based on key completion data to detect duplicates
         const hashData = {
            type: (eventData as any)?.type,
            sentFrom: (eventData as any)?.sentFrom,
            activityId: (eventData as any)?.activityId,
            activityTitle: (eventData as any)?.activityTitle,
            user: (eventData as any)?.user,
            timestamp: Math.floor(Date.now() / 1000), // Round to nearest second to allow for small timing differences
         };
         
         return JSON.stringify(hashData);
      },

      isActivitySubmitting(activityFilename: string): boolean {
         return !!this.submittingActivities[activityFilename];
      },

      async sendCompletionData(eventData, courseSlug, lessonSlug, activityFilename, updateLessonCache) {
         // Check activity-specific loading state first
         if (activityFilename && this.submittingActivities[activityFilename]) {
            return Promise.resolve({ status: 200, message: 'Duplicate submission prevented' });
         }

         // Fallback to global state check for backward compatibility
         if (this.isSendingCompletionData) {
            return Promise.resolve({ status: 200, message: 'Duplicate submission prevented' });
         }

         // Generate hash to detect duplicate events
         const eventHash = this.generateCompletionHash(eventData);
         if (this.processedCompletionHashes.has(eventHash)) {
            return Promise.resolve({ status: 200, message: 'Duplicate event prevented' });
         }

         // Add hash to processed set immediately to prevent race conditions
         this.processedCompletionHashes.add(eventHash);

         const authStore = useAuthStore();
         const config = useRuntimeConfig();

         const formData = new FormData();
         formData.append('xapi', JSON.stringify(eventData));
         formData.append('domain', this.domain);

         // Include course and lesson slugs for progress calculation
         if (courseSlug) {
            formData.append('course_slug', courseSlug);
         }
         if (lessonSlug) {
            formData.append('lesson_slug', lessonSlug);
         }

         // Append ipa_content_id if available (for reliable activity lookup using Google Drive ID)
         const ipaContentId = (eventData as any)?.ipaContentId;
         if (ipaContentId) {
            formData.append('ipa_content_id', ipaContentId);
         } else {
            console.warn('No ipa_content_id provided, backend will fall back to title-based lookup');
         }

         try {
            // Set activity-specific loading state
            if (activityFilename) {
               this.submittingActivities[activityFilename] = true;
            }
            // Also set global state for backward compatibility
            this.isSendingCompletionData = true;
            
            const result = await handleApiRequest(
               this,
               'isSendingCompletionData',
               'completionResponseStatus',
               () => axios.post(
                  `${this.lmsApiBaseurl}interactive-content/completion`,
                  formData,
                  {
                     headers: authStore.authHeaders,
                  },
               ),
               { ref: 'sendCompletionError', value: null }
            );

            // Check if the request was successful based on response status
            if (this.completionResponseStatus.statusCode && this.completionResponseStatus.statusCode >= 200 && this.completionResponseStatus.statusCode < 300) {
               // Update TanStack Query cache with returned activity_status and progress (if available)
               if (updateLessonCache) {
                  updateLessonCache((oldLesson: any) => {
                     if (!oldLesson) return oldLesson;

                     const updates: any = {};

                     // Update activities_status if returned
                     if (result?.activity_status) {
                        const existingActivities = oldLesson.activities_status || [];
                        updates.activities_status = [
                           ...existingActivities.filter((a: any) =>
                              a.activity_file_name !== result.activity_status.activity_file_name
                           ),
                           result.activity_status
                        ];
                     }

                     // Update lesson progress if returned
                     if (result?.lesson_completion_percent !== undefined) {
                        updates.lesson_completion_percent = result.lesson_completion_percent;
                     }

                     return { ...oldLesson, ...updates };
                  });
               }

               // Update Pinia store (H5pLoader reads activities_status from here)
               if (result?.activity_status) {
                  const lmsStore = useLmsStore();
                  if (lmsStore.currentLesson) {
                     const existingActivities = lmsStore.currentLesson.activities_status || [];
                     lmsStore.currentLesson.activities_status = [
                        ...existingActivities.filter((a: any) =>
                           a.activity_file_name !== result.activity_status.activity_file_name
                        ),
                        result.activity_status
                     ];
                  }
               }

               // Update Pinia store for sidebar progress bar
               if (result?.lesson_completion_percent !== undefined || result?.course_completion_percent !== undefined) {
                  const lmsStore = useLmsStore();

                  if (lessonSlug && result?.lesson_completion_percent !== undefined) {
                     lmsStore.updateLessonProgress(lessonSlug, result.lesson_completion_percent);
                  }
                  if (courseSlug && result?.course_completion_percent !== undefined) {
                     lmsStore.updateCourseProgress(courseSlug, result.course_completion_percent);
                  }
               } else if (courseSlug && lessonSlug) {
                  // Fallback: API didn't return progress, fetch separately
                  console.warn('No progress in response, falling back to refresh');
                  this.updateProgressAfterCompletion(courseSlug, lessonSlug);
               }

               return result || { status: this.completionResponseStatus.statusCode, message: this.completionResponseStatus.message };
            } 
            
               // Remove hash if request failed so it can be retried
               this.processedCompletionHashes.delete(eventHash);
               const errorMessage = this.completionResponseStatus.message || 'Failed to send completion data';
               this.sendCompletionError = errorMessage;
               throw new Error(errorMessage);
         } catch (error) {
            // Remove hash on any error so it can be retried
            this.processedCompletionHashes.delete(eventHash);
            
            // Re-throw the error to be handled by the component
            if (error instanceof Error) {
               this.sendCompletionError = error.message;
               throw error;
            } else {
               this.sendCompletionError = 'Failed to send completion data';
               throw new Error(this.sendCompletionError);
            }
         } finally {
            // Clear activity-specific loading state
            if (activityFilename) {
               this.submittingActivities[activityFilename] = false;
            }
            // Clear global state
            this.isSendingCompletionData = false;
         }
      },

      updateProgressAfterCompletion(courseSlug: string, lessonSlug: string) {
         // Non-blocking progress update - don't await or throw errors
         try {
            const lmsStore = useLmsStore();

            // Use setTimeout to make it truly non-blocking
            setTimeout(() => {
               // Update progress percentages
               lmsStore.refreshProgressFromAPI(courseSlug, lessonSlug).catch(error => {
                  console.error('Progress update failed after completion:', error);
               });

               // Update activities status for tooltips
               lmsStore.refreshActivitiesStatus(courseSlug, lessonSlug).catch(error => {
                  console.error('Activities status refresh failed after completion:', error);
               });
            }, 100); // Small delay to ensure completion is fully processed
         } catch (error) {
            console.error('Failed to trigger progress update:', error);
         }
      },

      async sendDebugData(debugData: unknown): Promise<void> {
         const authStore = useAuthStore();
         if (!authStore.accessToken) {
            console.error('Authentication token is missing for sendDebugData.');
            throw new Error('Authentication token is missing.');
         }

         const apiUrl = `${this.lmsApiBaseurl}interactive-content/debug`;
         try {
            await axios.post(apiUrl, debugData, {
               headers: authStore.authHeaders,
            });
         } catch (error) {
            console.error('Error sending debug data:', error);
            throw error;
         }
      },

   },
});

export default useInteractiveContentStore;
